import { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { execApi } from '../../../services/api';
import './styles.css';

// Wellness content usage in the mobile app, built on the RAW telemetry
// (DWH.mobile_tracking) rather than the four-field view — which is why type, scroll
// depth, area attribution, entry sources and sessions exist here at all.
//
// Ordered as an argument, not as a list of requests: what happens (funnel) → why
// (exposure) → is it improving (trend) → what works among what IS consumed
// (content) → the rest of the app for context. Every metric carries an ⓘ, because
// a number nobody can define is a number nobody should act on.
//
// Still genuinely missing, and said out loud on the page: button clicks, link
// clicks, answers, "this helped me", favourites, audio/video playback progress.

interface FunnelStage { id: string; label: string; users: number; of_previous: number; of_first: number }
interface ContentRow {
  item_id: string; item: string; item_type: string; type_label: string; category: string | null;
  views: number; unique_users: number; views_per_user: number;
  median_seconds: number; avg_seconds: number; total_minutes: number;
  engaged_views: number; engaged_rate: number;
  avg_scroll: number; completion_rate: number; skim_rate: number;
  prev_views: number; delta_views: number;
}
interface TypeRow {
  item_type: string; type_label: string; items: number; views: number;
  unique_users: number; median_seconds: number; completion_rate: number;
}
interface AreaRow {
  category: string; entries: number; unique_users: number;
  median_seconds: number; click_through: number; click_rate: number;
}
interface ScreenRow { screen: string; views: number; unique_users: number; median_seconds: number }
interface EntryRow { source: string; label: string; entries: number; unique_users: number; share: number }
interface SessionStats {
  sessions: number; users: number; sessions_per_user: number;
  sessions_with_wellness: number; wellness_session_rate: number; screens_per_session: number;
}
interface TrendPoint { bucket: string; entries: number; content_views: number; users: number }
interface Drill {
  period: string; period_label: string; granularity: string; data_from: string | null;
  funnel: FunnelStage[]; content: ContentRow[]; types: TypeRow[];
  areas: AreaRow[]; screens: ScreenRow[]; trend: TrendPoint[];
  entries: EntryRow[]; sessions: SessionStats;
  total_views: number; total_users: number; prev_total_views: number; prev_total_users: number;
  median_dwell: number; total_minutes: number;
}

const PERIODS = [
  { id: '7d', label: '7 ימים' },
  { id: '30d', label: '30 ימים' },
  { id: '90d', label: '90 ימים' },
  { id: 'all', label: 'הכל' },
];

// Every definition on the page lives here, so the page and the explanation can
// never drift apart.
const HELP = {
  views: 'כל פתיחה של מסך אזור או של פריט תוכן. אדם שנכנס ל"מרכז ההקלה" ופתח שני מאמרים = 3.',
  users: 'מטופלים שונים שנגעו בוולנס בתקופה. מי שנכנס עשר פעמים נספר פעם אחת.',
  medianDwell: 'מחצית מהצפיות בתוכן ארכו יותר מזה, ומחצית פחות. חציון ולא ממוצע — צפייה בודדת של 14 דקות מעוותת ממוצע של עשרים.',
  sessionRate: 'מתוך כל הביקורים באפליקציה — בכמה מהם בכלל נפתח משהו בוולנס. המשפך סופר אנשים, זה סופר ביקורים.',
  stageApp: 'מטופלים שנרשם להם לפחות מסך אחד באפליקציה בתקופה.',
  stageWellness: 'פתחו מסך של אזור — מרכז ההקלה, העוגן שלי, נבחרת המומחים וכו׳.',
  stageContent: 'פתחו פריט תוכן ממשי, ולא רק את רשימת האזור.',
  stageEngaged: 'נשארו בתוכן לפחות חצי דקה — קריאה ולא הצצה. הסף נקבע לפי חציון השהייה שנמדד בפועל.',
  colViews: 'כמה פעמים נפתח התוכן, כולל פתיחות חוזרות של אותו אדם.',
  colUnique: 'כמה אנשים שונים פתחו אותו.',
  colRepeat: 'צפיות חלקי אנשים — כמה פעמים בממוצע אדם חוזר לתוכן הזה.',
  colMedian: 'חציון שניות השהייה בתוכן הזה.',
  colEngaged: 'אחוז הצפיות שנמשכו 30 שניות ומעלה.',
  colScroll: 'ממוצע האחוז שנגלל בתוכן. 100% = הגיעו לתחתית.',
  colDone: 'אחוז הצפיות שגללו 90% ומעלה — התחליף ל"סיימו לצפות", שרלוונטי רק לאודיו ווידאו.',
  colMinutes: 'סך זמן הצפייה המצטבר בתוכן הזה, בדקות.',
  colDelta: 'ההפרש בצפיות מול תקופה קודמת באותו אורך. "חדש" = לא נצפה כלל בתקופה הקודמת.',
  areaClick: 'אחוז היציאות מהאזור שנעשו בלחיצה על פריט תוכן — עד כמה האזור באמת מוביל פנימה, ולא רק נפתח.',
  entrySource: 'המסך שממנו הגיעו למסך האזור, לפי מה שהאפליקציה עצמה מדווחת.',
  compare: 'התקופה שבחרת למעלה מושווית לתקופה באותו אורך בדיוק שקדמה לה מיד: 30 הימים האחרונים מול 30 הימים שלפניהם. ירוק = עלייה, אדום = ירידה. ב"הכל" אין תקופה קודמת ולכן אין השוואה.',
  exposure: 'מאיפה אנשים מגיעים לאזור הוולנס. האפליקציה מדווחת בכל כניסה מאיזה מסך היא הגיעה — מסך הבית, חזרה לאפליקציה מהרקע, או מעבר מאזור וולנס אחר. האחוז הוא חלקו של אותו מקור מכלל הכניסות לוולנס בתקופה. זה לא משפך אלא פילוח: כל הכניסות מתחלקות בין המקורות ומסתכמות ל-100%.',
  sessions: 'ביקור = פתיחה אחת של האפליקציה עד שסוגרים אותה. אותו אדם יכול לייצר עשרות ביקורים בחודש.',
  sessionsPerUser: 'ביקורים חלקי משתמשים — כמה פעמים בממוצע אדם פותח את האפליקציה בתקופה.',
  screensPerSession: 'כמה מסכים בממוצע נפתחים בביקור אחד. מספר נמוך = נכנסים, עושים דבר אחד ויוצאים.',
  activeUsers: 'מטופלים שפתחו את האפליקציה בתקופה — הבסיס שכל אחוזי החשיפה נמדדים מולו.',
  screens: 'כל מסכי האפליקציה לפי שימוש, כולל של מי שלא נגע בוולנס. חציון השהייה = כמה זמן נשארים במסך.',
  typeCard: 'הסיווג שהאפליקציה נותנת לתוכן. לחיצה מסננת את הטבלה שמתחת.',
};

function Info({ text }: { text: string }) {
  return <span className="wel-info" title={text} aria-label={text}>ⓘ</span>;
}

type SortKey = 'views' | 'unique_users' | 'median_seconds' | 'engaged_rate'
  | 'completion_rate' | 'avg_scroll' | 'total_minutes' | 'views_per_user' | 'delta_views';

const COLUMNS: { key: SortKey; label: string; help: string }[] = [
  { key: 'views', label: 'צפיות', help: HELP.colViews },
  { key: 'unique_users', label: 'ייחודיים', help: HELP.colUnique },
  { key: 'views_per_user', label: 'חזרות', help: HELP.colRepeat },
  { key: 'median_seconds', label: 'חציון שהייה', help: HELP.colMedian },
  { key: 'engaged_rate', label: 'שהייה 30ש׳+', help: HELP.colEngaged },
  { key: 'avg_scroll', label: 'עומק גלילה', help: HELP.colScroll },
  { key: 'completion_rate', label: 'הגיעו לסוף', help: HELP.colDone },
  { key: 'total_minutes', label: 'סה״כ דקות', help: HELP.colMinutes },
  { key: 'delta_views', label: 'מול קודם', help: HELP.colDelta },
];

const STAGE_HELP: Record<string, string> = {
  app: HELP.stageApp, wellness: HELP.stageWellness,
  content: HELP.stageContent, engaged: HELP.stageEngaged,
};

const dayLabel = (b: string) => b.slice(8, 10) + '/' + b.slice(5, 7);
const n2 = (n: number) => n.toFixed(2);

// The assignment side of the screen: our table, its schedule, and how many of the
// patients we assigned actually opened anything. Kept next to the usage funnel on
// purpose — an assignment nobody reads is the failure this page should surface.
type SyncCategory = { name: string; patients: number; viewers: number };
type SyncStatus = {
  config: { enabled: boolean; owner: string; hour: number; every_days: number; active_here: boolean };
  categories: SyncCategory[];
  patients: number;
  multi_category: number;
  last_write: string | null;
  reach_days: number;
  runs: { ts: string; status: string; error: string | null }[];
};

export function ExecWellness() {
  const [period, setPeriod] = useState('30d');
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [drill, setDrill] = useState<Drill | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortKey>('views');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    execApi.getWellness(period)
      .then((r) => { if (alive) setDrill(r.data as Drill); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const loadSync = () => execApi.getWellnessSync()
    .then((r) => setSync(r.data as SyncStatus))
    .catch(() => setSync(null));
  useEffect(() => { loadSync(); }, []);

  const toggleSync = async (enabled: boolean) => {
    if (!sync) return;
    setSyncBusy(true);
    try {
      await execApi.setWellnessSync({ ...sync.config, enabled });
      await loadSync();
    } finally { setSyncBusy(false); }
  };

  const runSyncNow = async () => {
    setSyncBusy(true);
    try { await execApi.runWellnessSync(); await loadSync(); }
    finally { setSyncBusy(false); }
  };

  const content = useMemo(() => {
    if (!drill) return [];
    const pool = typeFilter ? drill.content.filter((c) => c.item_type === typeFilter) : drill.content;
    return [...pool].sort((a, b) => (b[sort] as number) - (a[sort] as number));
  }, [drill, sort, typeFilter]);

  const maxViews = content.length ? Math.max(...content.map((c) => c.views)) : 1;
  const firstStage = drill?.funnel[0]?.users || 1;
  const maxScreen = drill?.screens.length ? Math.max(...drill.screens.map((s) => s.views)) : 1;

  // The comparison is always "this window vs the window of the same length right
  // before it", so the label says which window that is instead of leaving the
  // reader to guess what "previous period" meant.
  const PREV_LABEL: Record<string, string> = {
    '7d': 'מול 7 הימים שלפני כן',
    '30d': 'מול 30 הימים שלפני כן',
    '90d': 'מול 90 הימים שלפני כן',
    all: '',
  };
  const delta = (cur: number, prev: number) => {
    if (!prev) return { txt: 'אין תקופה קודמת להשוואה', cls: 'wel-flat' };
    const pct = ((cur - prev) / prev) * 100;
    return {
      txt: `${pct > 0 ? '+' : ''}${n2(pct)}% ${PREV_LABEL[period] || 'מול התקופה הקודמת'}`,
      sub: `${cur.toLocaleString('en-US')} מול ${prev.toLocaleString('en-US')}`,
      cls: pct > 0 ? 'wel-up' : pct < 0 ? 'wel-down' : 'wel-flat',
    };
  };

  if (loading && !drill) return <div className="wel-wrap"><div className="wel-empty">טוען…</div></div>;
  if (!drill) return <div className="wel-wrap"><div className="wel-empty">אין נתונים</div></div>;

  const dViews = delta(drill.total_views, drill.prev_total_views);
  const dUsers = delta(drill.total_users, drill.prev_total_users);
  const s = drill.sessions;

  return (
    <div className="wel-wrap">
      <div className="wel-header">
        <div className="wel-title-block">
          <h1 className="wel-title">וולנס — צריכת תוכן באפליקציה</h1>
          <span className="wel-chip">{drill.period_label}</span>
          {drill.data_from && <span className="wel-chip">נתונים מ-{drill.data_from}</span>}
        </div>
        <div className="wel-periods">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`wel-period ${period === p.id ? 'active' : ''}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {sync && (
        <div className="wel-section">
          <div className="wel-section-title">
            במיוחד בשבילך · שיוך תוכן לפי אבחנה
          </div>
          <div className="efy-head">
            <div className="efy-facts">
              <span><b>{sync.patients.toLocaleString('en-US')}</b> מטופלים משויכים</span>
              <span><b>{sync.multi_category.toLocaleString('en-US')}</b> ביותר מקטגוריה אחת</span>
              {sync.last_write && <span>עודכן לאחרונה {sync.last_write.slice(0, 16)}</span>}
            </div>
            <div className="efy-controls">
              <span className={`efy-state ${sync.config.enabled ? 'on' : 'off'}`}>
                {sync.config.enabled
                  ? `רענון אוטומטי פעיל · ${sync.config.every_days === 1 ? 'כל יום' : `כל ${sync.config.every_days} ימים`} בשעה ${String(sync.config.hour).padStart(2, '0')}:00`
                  : 'רענון אוטומטי כבוי'}
              </span>
              <button className="efy-btn" disabled={syncBusy}
                onClick={() => toggleSync(!sync.config.enabled)}>
                {sync.config.enabled ? 'כבה' : 'הפעל'}
              </button>
              <button className="efy-btn ghost" disabled={syncBusy} onClick={runSyncNow}>
                רענן עכשיו
              </button>
            </div>
          </div>

          <table className="efy-table">
            <thead>
              <tr>
                <th>קטגוריה</th>
                <th>מטופלים משויכים</th>
                <th>מתוכם פתחו תוכן ({sync.reach_days} יום)</th>
                <th>שיעור</th>
              </tr>
            </thead>
            <tbody>
              {sync.categories.map((c) => {
                const pct = c.patients ? (c.viewers / c.patients) * 100 : 0;
                return (
                  <tr key={c.name}>
                    <td>{c.name}</td>
                    <td className="efy-num">{c.patients.toLocaleString('en-US')}</td>
                    <td className="efy-num">{c.viewers.toLocaleString('en-US')}</td>
                    <td className="efy-num">
                      <span className="efy-bar" style={{ width: `${Math.min(100, pct * 4)}%` }} />
                      {n2(pct)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sync.runs.length > 0 && (
            <div className="efy-runs">
              ריצה אחרונה: {sync.runs[0].ts.slice(0, 16)} · {sync.runs[0].status === 'ok' ? 'הצליחה' : `נכשלה — ${sync.runs[0].error || ''}`}
            </div>
          )}
        </div>
      )}

      {/* 1 — what happens */}
      <div className="wel-section">
        <div className="wel-section-title">משפך · מה קורה</div>
        <div className="wel-funnel">
          {drill.funnel.map((st, i) => (
            <div key={st.id} className="wel-stage">
              <div className="wel-stage-fill" style={{ width: `${(st.users / firstStage) * 100}%` }} />
              <div className="wel-stage-row">
                <span className="wel-stage-users">{st.users.toLocaleString('en-US')}</span>
                <span className="wel-stage-label">{st.label}<Info text={STAGE_HELP[st.id] || ''} /></span>
                <span className="wel-stage-pct">
                  {i > 0 && <>{n2(st.of_previous)}% מהשלב הקודם<span className="wel-dim"> · {n2(st.of_first)}% מכולם</span></>}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="wel-section">
        <div className="wel-kpis">
          <div className="wel-kpi">
            <div className="wel-kpi-label">צפיות בוולנס<Info text={HELP.views} /></div>
            <div className="wel-kpi-value">{drill.total_views.toLocaleString('en-US')}</div>
            <div className={`wel-kpi-delta ${dViews.cls}`} title={HELP.compare}>
              {dViews.txt}<Info text={HELP.compare} />
            </div>
            {dViews.sub && <div className="wel-kpi-sub">{dViews.sub}</div>}
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">משתמשים בוולנס<Info text={HELP.users} /></div>
            <div className="wel-kpi-value">{drill.total_users.toLocaleString('en-US')}</div>
            <div className={`wel-kpi-delta ${dUsers.cls}`} title={HELP.compare}>
              {dUsers.txt}<Info text={HELP.compare} />
            </div>
            {dUsers.sub && <div className="wel-kpi-sub">{dUsers.sub}</div>}
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">חציון שהייה בתוכן<Info text={HELP.medianDwell} /></div>
            <div className="wel-kpi-value">{n2(drill.median_dwell)}<span className="wel-unit">שניות</span></div>
            <div className="wel-kpi-delta wel-flat">סה״כ {n2(drill.total_minutes)} דקות צפייה</div>
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">ביקורים שנגעו בוולנס<Info text={HELP.sessionRate} /></div>
            <div className="wel-kpi-value">{n2(s.wellness_session_rate)}<span className="wel-unit">%</span></div>
            <div className="wel-kpi-delta wel-amber">
              {s.sessions_with_wellness.toLocaleString('en-US')} מתוך {s.sessions.toLocaleString('en-US')} ביקורים
            </div>
          </div>
        </div>
      </div>

      {/* 2 — why it happens */}
      <div className="wel-section">
        <div className="wel-section-title">
          חשיפה · למה זה קורה<Info text={HELP.exposure} />
        </div>
        <div className="wel-sub">מאיפה הגיעו לוולנס — פילוח של כל הכניסות לפי מקור (מסתכם ל-100%)</div>
        <div className="wel-entries">
          {drill.entries.map((e) => (
            <div key={e.source} className="wel-entry">
              <div className="wel-entry-bar" style={{ width: `${e.share}%` }} />
              <div className="wel-entry-row">
                <span className="wel-entry-share">{n2(e.share)}%</span>
                <span className="wel-entry-label">{e.label}</span>
                <span className="wel-entry-n">{e.entries.toLocaleString('en-US')} כניסות · {e.unique_users} משתמשים</span>
              </div>
            </div>
          ))}
        </div>
        <div className="wel-kpis wel-kpis-mt">
          <div className="wel-kpi">
            <div className="wel-kpi-label">ביקורים באפליקציה<Info text={HELP.sessions} /></div>
            <div className="wel-kpi-value">{s.sessions.toLocaleString('en-US')}</div>
            <div className="wel-kpi-delta wel-flat">בתקופה שנבחרה</div>
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">ביקורים למשתמש<Info text={HELP.sessionsPerUser} /></div>
            <div className="wel-kpi-value">{n2(s.sessions_per_user)}</div>
            <div className="wel-kpi-delta wel-flat">בממוצע</div>
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">מסכים בביקור<Info text={HELP.screensPerSession} /></div>
            <div className="wel-kpi-value">{n2(s.screens_per_session)}</div>
            <div className="wel-kpi-delta wel-flat">בממוצע</div>
          </div>
          <div className="wel-kpi">
            <div className="wel-kpi-label">משתמשים פעילים<Info text={HELP.activeUsers} /></div>
            <div className="wel-kpi-value">{s.users.toLocaleString('en-US')}</div>
            <div className="wel-kpi-delta wel-flat">פתחו את האפליקציה</div>
          </div>
        </div>
        <div className="wel-note">
          לוולנס יש דלת אחת כמעט בלעדית. אם החשיפה היא הצוואר — נוגעים בו כאן, בנקודות כניסה
          נוספות, ולא בהוספת תוכן.
        </div>
      </div>

      {/* 3 — is it improving */}
      <div className="wel-section">
        <div className="wel-section-title">
          מגמה · האם זה משתפר ({drill.granularity === 'day' ? 'יומי' : 'שבועי'})
        </div>
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={drill.trend} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={dayLabel} stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="entries" name="כניסות לאזור" fill="#2563EB" radius={[3, 3, 0, 0]} />
              <Bar dataKey="content_views" name="צפיות בתוכן" fill="#8B5CF6" radius={[3, 3, 0, 0]} />
              <Line dataKey="users" name="משתמשים" stroke="#10B981" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 4 — what works among what IS consumed */}
      <div className="wel-section">
        <div className="wel-section-title">תוכן · מה עובד<Info text={HELP.typeCard} /></div>
        <div className="wel-types">
          {drill.types.map((t) => (
            <button key={t.item_type}
              onClick={() => setTypeFilter(typeFilter === t.item_type ? null : t.item_type)}
              className={`wel-type ${typeFilter === t.item_type ? 'active' : ''}`}>
              <span className="wel-type-name">{t.type_label}</span>
              <span className="wel-type-views">{t.views}</span>
              <span className="wel-type-meta">
                {t.items} פריטים · חציון {n2(t.median_seconds)}ש׳ · {n2(t.completion_rate)}% לסוף
              </span>
            </button>
          ))}
        </div>
        <div className="wel-note">
          זו הטקסונומיה של האפליקציה (מאמר / תמונה / אודיו / וידאו), לא זו של עדי
          (טיפ / מאמר / אודיו / שאלון / וידאו) — "טיפ" ו"שאלון" אינם קיימים בנתונים.
        </div>
      </div>

      <div className="wel-section">
        <div className="wel-section-title">
          כל התכנים
          {typeFilter && <button className="wel-clear" onClick={() => setTypeFilter(null)}>
            מסונן: {drill.types.find((t) => t.item_type === typeFilter)?.type_label} ✕</button>}
          <span className="wel-dim"> · לחיצה על כותרת עמודה ממיינת</span>
        </div>
        <div className="wel-scroll">
          <table className="wel-table">
            <thead>
              <tr>
                <th>תוכן</th>
                <th>סוג</th>
                <th>אזור</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={`num ${sort === c.key ? 'sorted' : ''}`}
                    title={c.help} onClick={() => setSort(c.key)}>
                    {c.label}{sort === c.key ? ' ↓' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.map((r) => (
                <tr key={r.item_id}>
                  <td className="wel-name">{r.item}</td>
                  <td><span className="wel-tag">{r.type_label}</span></td>
                  <td className="wel-muted">{r.category || '—'}</td>
                  <td className="num wel-bar-cell">
                    <div className="wel-bar" style={{ width: `${(r.views / maxViews) * 100}%` }} />
                    <span className="wel-bar-val">{r.views}</span>
                  </td>
                  <td className="num">{r.unique_users}</td>
                  <td className="num">{n2(r.views_per_user)}</td>
                  <td className="num">{n2(r.median_seconds)}ש׳</td>
                  <td className="num">{n2(r.engaged_rate)}%</td>
                  <td className="num">{n2(r.avg_scroll)}%</td>
                  <td className="num">{n2(r.completion_rate)}%</td>
                  <td className="num">{n2(r.total_minutes)}</td>
                  <td className={`num ${r.delta_views > 0 ? 'wel-up' : r.delta_views < 0 ? 'wel-down' : 'wel-flat'}`}>
                    {r.prev_views === 0 && r.views > 0 ? 'חדש' : (r.delta_views > 0 ? '+' : '') + r.delta_views}
                  </td>
                </tr>
              ))}
              {!content.length && <tr><td colSpan={12} className="wel-empty">אין צפיות בתקופה</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="wel-note">
          כל כותרת עמודה נושאת הסבר — עצור עליה עם העכבר. "עומק גלילה" ו"הגיעו לסוף" הם התחליף
          למדד ההשלמה שנועד לאודיו ווידאו: במאמרים ובתמונות, שהם כמעט כל הספרייה, הגלילה היא ההשלמה.
        </div>
      </div>

      <div className="wel-section">
        <div className="wel-section-title">אזורים · האם האזור מוביל לתוכן</div>
        <table className="wel-table">
          <thead>
            <tr>
              <th>אזור</th>
              <th className="num" title="כמה פעמים נפתח מסך האזור">כניסות</th>
              <th className="num" title="כמה אנשים שונים פתחו אותו">ייחודיים</th>
              <th className="num" title="חציון השניות במסך האזור עצמו — זה מסך עיון, לא קריאה">חציון שהייה</th>
              <th className="num" title={HELP.areaClick}>יצאו לתוכן</th>
            </tr>
          </thead>
          <tbody>
            {drill.areas.map((a) => (
              <tr key={a.category}>
                <td className="wel-name">{a.category}</td>
                <td className="num">{a.entries}</td>
                <td className="num">{a.unique_users}</td>
                <td className="num">{n2(a.median_seconds)}ש׳</td>
                <td className="num">{n2(a.click_rate)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 5 — context */}
      <div className="wel-section">
        <div className="wel-section-title">כל האפליקציה · הקשר<Info text={HELP.screens} /></div>
        <table className="wel-table">
          <thead>
            <tr>
              <th>מסך</th>
              <th className="num">צפיות</th>
              <th className="num">משתמשים</th>
              <th className="num">חציון שהייה</th>
            </tr>
          </thead>
          <tbody>
            {drill.screens.map((sc) => (
              <tr key={sc.screen}>
                <td className="wel-name">{sc.screen}</td>
                <td className="num wel-bar-cell">
                  <div className="wel-bar" style={{ width: `${(sc.views / maxScreen) * 100}%` }} />
                  <span className="wel-bar-val">{sc.views.toLocaleString('en-US')}</span>
                </td>
                <td className="num">{sc.unique_users.toLocaleString('en-US')}</td>
                <td className="num">{n2(sc.median_seconds)}ש׳</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="wel-note">
          זו התשובה ל"מה עשו כל השאר" — מסכי האפליקציה מדורגים לפי שימוש, כולל של מי שלא נגע בוולנס.
        </div>
      </div>
    </div>
  );
}
